/**
 * One chat turn: send the conversation to the `ai` function, stream the answer,
 * run the tools Claude asks for on this device and send their results back,
 * until Claude answers. The conversation only grows (append-only): earlier
 * turns, thinking blocks included, are sent back exactly as received.
 */
import type { Id } from '@nexus/core';
import type { Store } from '@nexus/store';
import type {
  AiErrorCode,
  AiEvent,
  AiModel,
  AiRequest,
  AiUsage,
  ContentBlockParam,
  MessageParam,
  StopReason,
} from './protocol';
import { ActionError, checkAction, isAction, type CheckedAction } from './actions';
import { runTool, toolContext, type ToolContext } from './tools';

export type Transport = (req: AiRequest, signal?: AbortSignal) => Promise<Response>;

export class AiError extends Error {
  constructor(
    readonly code: AiErrorCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'AiError';
  }
}

/** Server-Sent Events from a fetch response, one parsed event per `data:` line. */
export async function* readEvents(res: Response): AsyncGenerator<AiEvent> {
  if (!res.body) return;
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    let end: number;
    while ((end = buffer.indexOf('\n\n')) >= 0) {
      const chunk = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      const data = chunk
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trimStart())
        .join('\n');
      if (data) yield JSON.parse(data) as AiEvent;
    }
  }
}

async function failure(res: Response): Promise<AiError> {
  const body = (await res.json().catch(() => null)) as {
    error?: AiErrorCode;
    message?: string;
  } | null;
  if (body?.error) return new AiError(body.error, body.message);
  if (res.status === 401) return new AiError('signed-out');
  return new AiError('api', `HTTP ${res.status}`);
}

export interface TurnCallbacks {
  onText?: (text: string) => void;
  onTool?: (name: string) => void;
  onUsage?: (usage: AiUsage) => void;
}

export interface TurnResult {
  /** The whole conversation after this turn (input plus the new messages). */
  messages: MessageParam[];
  /** All text Claude wrote this turn. */
  text: string;
  /** Pages read while answering. */
  sources: Id[];
  stopReason: StopReason | null;
  /**
   * Claude proposed actions: the turn stops here. Each proposal needs a
   * tool_result (see `outcome`) before the conversation can go on; `results`
   * holds the results of the other tool calls of that turn.
   */
  pending?: { results: ContentBlockParam[]; proposals: Proposal[] };
}

export interface Proposal {
  callId: string;
  action: CheckedAction;
  /** Claude needs the result for a next step: continue right after the decisions. */
  continueAfter: boolean;
}

/** The tool_result for a decided proposal. */
export const outcome = (callId: string, text: string, isError = false): ContentBlockParam => ({
  type: 'tool_result',
  tool_use_id: callId,
  content: text,
  ...(isError ? { is_error: true } : {}),
});

export const DECLINED = 'The user declined this proposal; nothing was changed.';
export const NOT_DECIDED = 'Not done: the user moved on without confirming.';

const MAX_STEPS = 12;

export type DoneEvent = Extract<AiEvent, { type: 'done' }>;

/** One request to the `ai` function: stream its events, return the finished turn. */
export async function callOnce(
  transport: Transport,
  req: AiRequest,
  signal?: AbortSignal,
  cb: TurnCallbacks = {},
): Promise<DoneEvent> {
  let res: Response;
  try {
    res = await transport(req, signal);
  } catch (e) {
    if (signal?.aborted || e instanceof AiError) throw e;
    throw new AiError('network', e instanceof Error ? e.message : String(e));
  }
  if (!res.ok) throw await failure(res);

  let done: DoneEvent | null = null;
  for await (const event of readEvents(res)) {
    if (event.type === 'text') cb.onText?.(event.text);
    else if (event.type === 'tool') cb.onTool?.(event.name);
    else if (event.type === 'error') throw new AiError(event.error, event.message);
    else done = event;
  }
  if (!done) throw new AiError('network', 'The answer was cut off.');
  cb.onUsage?.(done.usage);
  return done;
}

export async function runTurn(opts: {
  transport: Transport;
  store: Store;
  model: AiModel;
  /** The conversation so far, ending with the new user message. */
  messages: MessageParam[];
  signal?: AbortSignal;
  callbacks?: TurnCallbacks;
  /** For tests: a fixed tool context. */
  context?: ToolContext;
}): Promise<TurnResult> {
  const { transport, store, model, signal, callbacks: cb = {} } = opts;
  const messages = [...opts.messages];
  const sources: Id[] = [];
  let text = '';
  let ctx = opts.context;

  for (let step = 0; step < MAX_STEPS; step++) {
    const done = await callOnce(transport, { mode: 'chat', model, messages }, signal, {
      ...cb,
      onText: (t) => {
        text += t;
        cb.onText?.(t);
      },
    });

    messages.push({ role: 'assistant', content: done.content as ContentBlockParam[] });
    const calls = done.content.filter((b) => b.type === 'tool_use');
    if (done.stop_reason === 'refusal') throw new AiError('refusal');
    if (done.stop_reason !== 'tool_use' || calls.length === 0)
      return { messages, text, sources, stopReason: done.stop_reason };
    if (done.stop_reason === 'tool_use' && calls.some((c) => c.input == null))
      throw new AiError('api', 'Incomplete tool call.');

    ctx ??= await toolContext(store);
    const results: ContentBlockParam[] = [];
    const proposals: Proposal[] = [];
    for (const call of calls) {
      if (isAction(call.name)) {
        // Actions are only checked now; they run when the user confirms.
        try {
          proposals.push({
            callId: call.id,
            action: await checkAction(ctx, call.name, call.input),
            continueAfter: (call.input as { continue_after?: unknown })?.continue_after === true,
          });
        } catch (e) {
          if (!(e instanceof ActionError)) throw e;
          results.push({
            type: 'tool_result',
            tool_use_id: call.id,
            content: e.message,
            is_error: true,
          });
        }
        continue;
      }
      const out = await runTool(ctx, call.name, call.input);
      for (const id of out.read ?? []) if (!sources.includes(id)) sources.push(id);
      results.push({
        type: 'tool_result',
        tool_use_id: call.id,
        content: out.content,
        ...(out.isError ? { is_error: true } : {}),
      });
    }
    // Proposals wait for the user; the other results go back with the outcome.
    if (proposals.length)
      return {
        messages,
        text,
        sources,
        stopReason: done.stop_reason,
        pending: { results, proposals },
      };
    // All results of one assistant turn go back in a single user message.
    messages.push({ role: 'user', content: results });
    // Text after the tool calls starts a new paragraph.
    if (text && !text.endsWith('\n\n')) {
      const sep = text.endsWith('\n') ? '\n' : '\n\n';
      text += sep;
      cb.onText?.(sep);
    }
  }
  throw new AiError('api', 'Too many steps without an answer.');
}

/**
 * The context Claude gets with each question: the date, and the page that is
 * open (unless it is hidden from the AI).
 */
export function contextText(opts: {
  now: Date;
  language: string;
  page?: { id: Id; title: string } | null;
  /** The main places by name (Taken, Projecten, Vergaderingen, Inbox), for proposals. */
  places?: { name: string; id: Id }[];
}): string {
  const { now, language, page, places = [] } = opts;
  const date = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(now);
  const lines = [`Now: ${date} (app language: ${language})`];
  if (page) lines.push(`Open page: [[${page.title || 'Untitled'}]] page_id=${page.id}`);
  if (places.length)
    lines.push(`Places: ${places.map((p) => `${p.name} page_id=${p.id}`).join('; ')}`);
  return `<context>\n${lines.join('\n')}\n</context>`;
}
